import type { QueryClient } from "@tanstack/react-query";
import type { EntityChangeNotification } from "../types/signalr";

const RELATED_ENTITIES: Record<string, string[]> = {
  orders: ["dashboard", "payments", "wallets", "greenhouse-stock-available", "agroforestry-stock"],
  payments: ["orders", "wallets", "dashboard", "greenhouse-stock-available", "agroforestry-stock"],
  wallets: ["orders", "payments", "customers"],
  inputs: ["input-batches", "unified-store-inventory"],
  "input-batches": ["inputs", "unified-store-inventory"],
  sowingInstructions: ["dashboard", "orders", "seed-batches-by-order", "greenhouse-stock-available"],
  beds: ["dashboard", "bays", "greenhouses"],
  bays: ["dashboard", "greenhouses"],
  greenhouses: ["dashboard"],
  "sales-invoices": ["dashboard", "orders", "credit-notes", "journal-entries"],
  "journal-entries": ["dashboard", "accounts", "bank-accounts"],
  "bank-transactions": ["bank-accounts"],
  requisitions: ["pending-approvals", "stock-movements"],
  setupApprovals: ["cropTypes", "cropVarieties", "varietyPricings"],
  importJob: ["customers"],
  "agroforestry-receipts": [
    "dashboard",
    "agroforestry-stock",
    "agroforestry-receipts-pending",
    "agroforestry-receipts",
    "greenhouse-stock-available",
  ],
  "production-discard": [
    "dashboard",
    "agroforestry-stock",
    "greenhouse-stock",
    "greenhouse-stock-available",
    "greenhouse-stock-entries",
    "sowingInstructions",
  ],
  "dispatch-records": [
    "dashboard",
    "dispatch",
    "trips",
    "reconciliation-queue",
    "delivery-plans",
    "orders",
    "greenhouse-stock-available",
    "greenhouse-stock-entries",
    "agroforestry-stock",
  ],
  greenhouseTransfers: ["dashboard", "greenhouse-stock-available", "greenhouse-stock-entries"],
  "walk-in-orders": [
    "greenhouse-stock-available",
    "greenhouse-stock-entries",
    "agroforestry-stock",
  ],
};

export function handleEntityChange(
  queryClient: QueryClient,
  notification: EntityChangeNotification
) {
  queryClient.invalidateQueries({ queryKey: [notification.entityType] });

  // Dev-only canary: if the backend just told us an entity type changed but
  // nothing in the cache matches that key, the frontend query key and the
  // backend EntityNameResolver mapping have drifted. Surface it loudly so
  // whoever owns the dropdown notices during development.
  if (import.meta.env.DEV) {
    const matches = queryClient
      .getQueryCache()
      .findAll({ queryKey: [notification.entityType] });
    if (matches.length === 0) {
      console.warn(
        `[realtime] No subscribed query matches entityType "${notification.entityType}". ` +
          `Either the backend EntityNameResolver mapping or the frontend queryKey is wrong, ` +
          `or this entity simply isn't displayed on the current page.`
      );
    }
  }

  const related = RELATED_ENTITIES[notification.entityType];
  if (related) {
    related.forEach((relatedKey) => {
      queryClient.invalidateQueries({ queryKey: [relatedKey] });
    });
  }
}

export function invalidateAllOnReconnect(queryClient: QueryClient) {
  queryClient.invalidateQueries();
}
